import { createReducer, on } from "@ngrx/store";
import { StorageItemWithIngredient } from "../../../shared/models/storage-item.model";
import * as StorageActions from "./storage.actions"
import { IngredientLocation } from "../../../shared/enums/ingredient-location.enum";
import { ExpiryStatus } from "../../../shared/enums/storage.enum";
import { QuotaInfo } from "../../../shared/models/quota-info.model";
import { IngredientCategory } from "../../../shared/enums/ingredient-category.enum";

export interface StorageState {
    items: StorageItemWithIngredient[];
    isLoading: boolean;
    isStorageQuotaLoading: boolean;
    error: any;
    searchTerm: string;
    filters: StorageFilterMap;
    quota: QuotaInfo | null;
}

export interface StorageFilterMap {
  category: IngredientCategory[];
  location: IngredientLocation[];
  is_opened: boolean | null;
  expiry: ExpiryStatus | null;
  is_leftover: boolean | null;
}

export const initialState: StorageState = {
  items: [],
  isLoading: false,
  isStorageQuotaLoading: false,
  error: null,
  filters: {
    category: [],
    location: [],
    is_opened: null,
    expiry: null,
    is_leftover: null
  },
  searchTerm: '',
  quota: null
};
  
export const storageReducer = createReducer(
    initialState,
    on(StorageActions.loadStorageItems, state => ({ ...state, isLoading: true })),
    on(StorageActions.loadStorageItemsSuccess, (state, { items }) => ({ ...state, isLoading: false, items })),
    on(StorageActions.loadStorageItemsFailure, (state, { error }) => ({ ...state, isLoading: false, error })),

    on(StorageActions.loadStorageQuota, state => ({ ...state, isStorageQuotaLoading: true })),
    on(StorageActions.loadStorageQuotaSuccess, (state, { quota }) => ({
      ...state,
      isStorageQuotaLoading: false,
      quota
    })),
    on(StorageActions.loadStorageQuotaFailure, (state) => ({
      ...state,
      quota: null,
      isStorageQuotaLoading: false,
}   )),

    on(StorageActions.addStorageItem, state => ({ ...state, isLoading: true })),
    on(StorageActions.addStorageItemSuccess, (state, { item }) => ({ ...state, isLoading: false, items: [...state.items, item] })),
    on(StorageActions.addStorageItemFailure, (state, { error }) => ({ ...state, isLoading: false, error })),

    on(StorageActions.updateStorageItem, state => ({ ...state, isLoading: true })),
    on(StorageActions.updateStorageItemSuccess, (state, { item }) => ({
      ...state,
      isLoading: false,
      items: state.items.map(i => i.storage_id === item.storage_id ? item : i)
    })),
    on(StorageActions.updateStorageItemFailure, (state, { error }) => ({ ...state, isLoading: false, error })),

    on(StorageActions.deleteStorageItem, state => ({ ...state, isLoading: true })),
    on(StorageActions.deleteStorageItemSuccess, (state, { id }) => ({
      ...state,
      isLoading: false,
      items: state.items.filter(i => i.storage_id !== id)
    })),
    on(StorageActions.deleteStorageItemFailure, (state, { error }) => ({ ...state, isLoading: false, error })),
    on(StorageActions.setStorageFilters, (state, { filters }) => ({
      ...state,
      filters
    })),
    on(StorageActions.clearStorageFilters, (state) => ({
      ...state,
      filters: {
        category: [],
        location: [],
        is_opened: null,
        expiry: null,
        is_leftover: null
      }
    })),
    on(StorageActions.resetStorageFilters, (state) => ({
      ...state,
      filters: {
        category: [],
        location: [],
        is_opened: null,
        expiry: null,
        is_leftover: null
      }
    })),
    on(StorageActions.updateStorageSearchTerm, (state, { term }) => ({
      ...state,
      searchTerm: term
    })),
    on(StorageActions.resetStorageSearchTerm, (state) => ({
      ...state,
      searchTerm: ''
    })),
    on(StorageActions.bulkAddStorageItems, (state) => ({
      ...state,
      isLoading: true,
      error: null
    })),
    on(StorageActions.bulkAddStorageItemsSuccess, (state, { items }) => ({
      ...state,
      isLoading: false,
      items: [...state.items, ...items]
    })),

    on(StorageActions.bulkAddStorageItemsFailure, (state, { error }) => ({
      ...state,
      isLoading: false,
      error
    })),
    on(StorageActions.bulkUpdateStorageItems, (state) => ({
      ...state,
      isLoading: true,
      error: null
    })),
    on(StorageActions.bulkUpdateStorageItemsSuccess, (state, { items }) => ({
      ...state,
      isLoading: false,
      items: state.items.map(existing =>
        items.find(u => u.storage_id === existing.storage_id) || existing
      )
    })),
    on(StorageActions.bulkUpdateStorageItemsFailure, (state, { error }) => ({
      ...state,
      isLoading: false,
      error
    })),
    on(StorageActions.bulkDeleteStorageItems, (state) => ({
      ...state,
      isLoading: true,
      error: null
    })),
    on(StorageActions.bulkDeleteStorageItemsSuccess, (state, { ids }) => ({
      ...state,
      isLoading: false,
      items: state.items.filter(item => !ids.includes(item.storage_id))
    })),
    on(StorageActions.bulkDeleteStorageItemsFailure, (state, { error }) => ({
      ...state,
      isLoading: false,
      error
    })),
    on(StorageActions.resetStorage, () => initialState)
);
  

  